import {
    CWTSchema,
    CWTSchemaField,
    CWTSchemaList,
    CWTSchemaListItem,
    CWTSchemaBase,
    CWTSchemaSection,
    CWTSchemaSectionGroup,
    ListType,
    SectionType,
    CWTSchemaProperty,
    FieldType,
    CWTRef,
    CWTSchemaTable,
    TableType,
    CWTSchemaTableRow,
    CWTSchemaPropertySummary,
    CWTSchemaPropertiesSummary,
    TableRowStyles,
    CWTFieldReconciliation,
} from '../../../types/custom-worksheet-v2';
import { getReference } from '../../../components/custom-worksheet/utils';
import { get, set, unset } from 'lodash';
import { ReferenceMap } from './slice';
import { NUMERIC_TYPES } from '../../../constants';
import traverse from 'traverse';

/**
 * Checks if the given ref is reference to a local property in a schema.
 *
 * @param ref - the ref to check
 * @returns `true` if the string is a local property reference, `false` otherwise
 */
export function isReference(ref: unknown): ref is CWTRef {
    if (typeof ref !== 'object' || ref === null || Array.isArray(ref)) {
        return false;
    }

    // @ts-ignore
    if ('ref' in ref && typeof ref.ref === 'string') {
        return true;
    }

    return false;
}

/**
 * Checks if the given value is a CWTSchemaProperty.
 *
 * @param p - the value to check
 * @returns `true` if the value is a CWTSchemaProperty, `false` otherwise
 */
function isCWTSchemaProperty(p: unknown): p is CWTSchemaProperty {
    return typeof p === 'object' && p !== null && !Array.isArray(p) && 'id' in p;
}

/**
 * Get cwt schema property by its id
 *
 * @param schema CWTSchema
 * @param id Property id
 * @returns CWTSchemaProperty | null
 */
export function getProperty(schema: CWTSchema, id: string): CWTSchemaProperty | null {
    const property = get(schema, id);

    if (!property) {
        return null;
    }

    return property;
}

/**
 * Get properties for a property by its id
 *
 * @param schema CWTSchema
 * @param id Property id
 * @returns { [key: string]: CWTSchemaProperty } | null
 */
export function getProperties(schema: CWTSchema, id: string): { [key: string]: CWTSchemaProperty } | null {
    const properties = get(schema, id);

    if (!properties) {
        return null;
    }

    return properties;
}

/**
 * Checks if a cwt schema property is a list
 *
 * @param property
 * @returns property is CWTSchemaList
 */
export function isCWTSchemaList(property: CWTSchemaProperty): property is CWTSchemaList {
    return property.type === ListType.LIST;
}

/**
 * Checks if a cwt schema property is a list item
 *
 * @param property
 * @returns property is CWTSchemaListItem
 */
export function isCWTSchemaListItem(property: CWTSchemaProperty): property is CWTSchemaListItem {
    return property.type === ListType.LIST_ITEM;
}

/**
 * Checks if a cwt schema property is a section group
 *
 * @param property
 * @returns property is CWTSchemaSectionGroup
 */
export function isCWTSchemaSectionGroup(property: CWTSchemaProperty): property is CWTSchemaSectionGroup {
    return property.type === SectionType.SECTION_GROUP;
}

/**
 * Checks if a cwt schema property is a section
 *
 * @param property
 * @returns property is CWTSchemaSection
 */
export function isCWTSchemaSection(property: CWTSchemaProperty): property is CWTSchemaSection {
    return property.type === SectionType.SECTION;
}

/**
 * Checks if a cwt schema property is a field
 *
 * @param property
 * @returns property is CWTSchemaField
 */
export function isCWTSchemaField(property: CWTSchemaProperty): property is CWTSchemaField | (CWTSchemaField & { styles: TableRowStyles }) {
    return (
        !isCWTSchemaSectionGroup(property) &&
        !isCWTSchemaSection(property) &&
        !isCWTSchemaList(property) &&
        !isCWTSchemaListItem(property) &&
        !isCWTSchemaTable(property) &&
        !isCWTSchemaTableRow(property)
    );
}

/**
 * Checks if a cwt schema property is a table
 *
 * @param property
 * @returns property is CWTSchemaTable
 */
export function isCWTSchemaTable(property: unknown): property is CWTSchemaTable {
    return isCWTSchemaProperty(property) && property.type === TableType.TABLE;
}

/**
 * Checks if a cwt schema property is a table row
 *
 * @param property
 * @returns property is CWTSchemaTableRow
 */
export function isCWTSchemaTableRow(property: CWTSchemaProperty): property is CWTSchemaTableRow {
    return property.type === TableType.TABLE_ROW;
}

/**
 * Function to sort and map schema properties
 *
 * @param properties
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sortByOrder<T extends CWTSchemaBase>(properties: T[] | Record<string, T>) {
    return Object.entries(properties)
        .sort((a, b) => (a[1].order > b[1].order ? 1 : -1))
        .map(([, value]) => value);
}

/**
 * Get all references to be deleted when a property gets deleted
 * All references = property id + all its children property ids
 *
 * @param property CWTSchemaProperty
 * @returns Array of all references to be deleted
 */
export function getToDeleteReferences(property: CWTSchemaProperty): string[] {
    const toDeleteReferences: string[] = [getReference(property.id)];

    if (!isCWTSchemaField(property)) {
        traverse(property.properties).forEach(function (s) {
            // Only process properties
            if (isCWTSchemaProperty(s) && s.type !== ListType.LIST_ITEM && s.type !== TableType.TABLE_ROW) {
                const reference = getReference(s.id);
                toDeleteReferences.push(reference);
            }
        });
    }

    return toDeleteReferences;
}

/**
 * Reorder all child properties according to its new order value
 *
 * @param properties { [key: string]: CWTSchemaProperty }
 * @returns Sorted child properties
 */
export function reorderProperties(properties: { [key: string]: CWTSchemaProperty }): { [key: string]: CWTSchemaProperty } {
    // Update properties order
    Object.entries(properties)
        // Sort properties according to their current order
        .sort(([, aValue], [, bValue]) => (aValue.order > bValue.order ? 1 : -1))
        // Then update the order so that it equals to its new position index
        .forEach(([key], idx) => {
            properties[key].order = idx + 1;
        });

    return properties;
}

/**
 * Function to generate new id using new reference
 *
 * @param id Previous property id
 * @param newReference New property reference
 * @returns New id
 */
export function generateNewId(id: string, newReference: string): string {
    return `${id.split('.').slice(0, -1).join('.')}.${newReference}`;
}

/**
 * Update property reference and id that contains the previous reference
 *
 * @param schema CWTSchema
 * @param id Property id
 * @param newReference New property reference
 */
export function updatePropertyReference(schema: CWTSchema, property: CWTSchemaProperty, newId: string, newReference: string): CWTSchema {
    const clone = traverse(schema).clone();

    const prevReference = getReference(property.id);

    // Add property with new reference into schema
    set(clone, newId, property);

    // Remove property with prev reference from schema
    unset(clone, property.id);

    // Update dependent fields/calculations with new reference
    updateDependentFields(clone, property.id, newId);

    // If property is a field and it belongs in a table, update summary object with new key value
    if (isCWTSchemaField(property)) {
        const parentProperty = getProperty(clone, property.id.split('.').slice(0, -2).join('.'));

        if (parentProperty && isCWTSchemaTableRow(parentProperty)) {
            const tableId = parentProperty.id.split('.').slice(0, -2).join('.');
            const table = getProperty(clone, tableId);

            if (table && isCWTSchemaTable(table) && table.summary !== null) {
                const currentSummary = table.summary.properties;

                delete currentSummary[prevReference];

                const summary = buildSummaryObject(newId, property.type as FieldType, canSummarise(property.type));
                currentSummary[newReference] = summary;

                set(clone, `${tableId}.summary.properties`, currentSummary);
            }
        }

        if (parentProperty && isCWTSchemaSection(parentProperty)) {
            let summary = parentProperty.summary?.properties[getReference(parentProperty.id)];

            if (summary && summary.type === property.type) {
                summary = removeFieldFromSectionSummary(summary, property.id);
                summary = addFieldToSectionSummary(summary, newId);
                set(clone, `${parentProperty.id}.summary.properties`, { [getReference(parentProperty.id)]: summary });
            }
        }
    }

    // If property is a section and it has a summary, update key of summary object
    else if (isCWTSchemaSection(property) && property.summary) {
        const constraintType = property.summary.properties[prevReference]?.type;
        const summary = updateSectionSummary(property, constraintType, prevReference);

        set(clone, property.id, {
            ...property,
            summary: summary === null ? null : { properties: { [getReference(property.id)]: summary } },
        });
    }

    // If property is a table and it has a summary, update key of summary object
    else if (isCWTSchemaTable(property) && property.summary) {
        const summaryProperties: { [key: string]: CWTSchemaPropertySummary | null } = {};
        const tableRows = property.properties.row.properties;

        // Go through every table row, build summary object
        Object.keys(tableRows).forEach((key) => {
            const summary = buildSummaryObject(tableRows[key].id, tableRows[key].type, canSummarise(tableRows[key].type));
            if (summary !== null) {
                summaryProperties[getReference(key)] = summary;
            }
        });

        set(clone, `${property.id}.summary`, { properties: summaryProperties });
    }

    return clone;
}

/**
 * Add list
 *
 * @param schema CWTSchema
 * @param name New list name
 * @returns CWTSchemaList
 */
export function addList(id: string, name: string, order: number): CWTSchemaList {
    // Build the new list
    return {
        id,
        name,
        order,
        description: null,
        readOnly: false,
        type: ListType.LIST,
        summary: null,
        properties: {
            item: {
                id: `${id}.properties.item`,
                name: 'Item',
                type: ListType.LIST_ITEM,
                description: null,
                readOnly: true,
                order: 1,
                properties: {},
            },
        },
    };
}

/**
 * Checks if a field belongs to a collection
 * @param schema Schema to check against
 * @param field Field to check for
 * @returns The collection the field belongs to, or null if the field is not part of a collection
 */
export function isInCollection(
    schema: CWTSchema,
    field: CWTSchemaProperty | CWTSchemaPropertySummary | string,
    highestCollection = false
): CWTSchemaList | CWTSchemaTable | null {
    const id = typeof field === 'string' ? field : field.id;

    const nodes = id.split('.');

    let node = nodes[0];

    let collection: CWTSchemaList | CWTSchemaTable | null = null;
    for (const n of nodes.slice(1)) {
        const property = getProperty(schema, node);

        if (property && (isCWTSchemaList(property) || isCWTSchemaTable(property))) {
            if (highestCollection) {
                return property;
            }
            collection = property;
        }
        node += `.${n}`;
    }

    return collection;
}

/**
 * Add section group
 *
 * @param schema CWTSchema
 * @param name New section group name
 * @param parentId Parent id that section group belongs to
 * @returns CWTSchemaSectionGroup
 */
export function addSectionGroup(id: string, name: string, order: number): CWTSchemaSectionGroup {
    return {
        id,
        name,
        order,
        description: null,
        readOnly: false,
        type: SectionType.SECTION_GROUP,
        properties: {},
    };
}

/**
 * Add section to a section group
 *
 * @param schema CWTSchema
 * @param name New section name
 * @returns CWTSchemaSection
 */
export function addSection(id: string, name: string, order: number): CWTSchemaSection {
    return {
        id,
        name,
        summary: null,
        order,
        description: null,
        readOnly: false,
        type: SectionType.SECTION,
        properties: {},
        additionalFields: null,
    };
}

/**
 * Add new field
 *
 * @param id Field id
 * @param name New field name
 * @param parentProperties
 * @returns CWTSchemaField
 */
export function addField(id: string, name: string, order: number): CWTSchemaField {
    return {
        id,
        name,
        description: null,
        helperText: null,
        order,
        readOnly: false,
        type: FieldType.MONEY,
        pinned: false,
        canPin: false,
        canRename: false,
        defaultValue: null,
        dependentFields: [],
        calculation: null,
        hidden: false,
    };
}

/**
 * Add table to schema
 *
 * @param schema CWTSchema
 * @param name New table name
 * @returns CWTSchemaTable
 */
export function addTable(id: string, name: string, order: number): CWTSchemaTable {
    return {
        id,
        name,
        order: order,
        description: null,
        readOnly: false,
        type: TableType.TABLE,
        summary: null,
        properties: {
            row: {
                id: `${id}.properties.row`,
                name: 'Row',
                type: TableType.TABLE_ROW,
                description: null,
                readOnly: true,
                order: 1,
                properties: {},
            },
        },
        config: { minRows: 0 },
    };
}

/**
 * Get existing pinned field within a section
 *
 * @param section CWTSchemaSection
 * @return Pinned field id if exists
 */
export function getCurrentPinnedField(section: CWTSchemaSection): string | undefined {
    const fields = section.properties;
    let currentPinnedFieldId: string | undefined;

    for (const key of Object.keys(fields)) {
        if (fields[key].pinned) {
            currentPinnedFieldId = fields[key].id;
            break;
        }
    }

    return currentPinnedFieldId;
}

/**
 * Build reference map with all references within the template
 *
 * @param schema CWTSchema
 * @returns ReferenceMap
 */
export function buildReferenceMap(schema: CWTSchema): ReferenceMap {
    const references: ReferenceMap = {};

    traverse(schema).forEach(function (s) {
        // Only process properties and the type is not a list item
        if (isCWTSchemaProperty(s) && s.type !== ListType.LIST_ITEM && s.type !== TableType.TABLE_ROW) {
            const reference = getReference(s.id);
            references[reference] = true;
        }
    });

    return references;
}

/**
 * Update table to allow summary row
 *
 * @param schema CWTSchema
 * @param id Property id
 */
export function updateTableShowSummary(table: CWTSchemaTable): CWTSchemaPropertiesSummary | null {
    if (table.summary !== null) {
        return null;
    }

    const summaryProperties: { [key: string]: CWTSchemaPropertySummary | null } = {};
    const tableRows = table.properties.row.properties;

    // Go through every table row, build summary object
    Object.keys(tableRows).forEach((key) => {
        const summary = buildSummaryObject(tableRows[key].id, tableRows[key].type, canSummarise(tableRows[key].type));
        if (summary !== null) {
            summaryProperties[getReference(key)] = summary;
        }
    });

    return { properties: summaryProperties };
}

/**
 * Remove deleted additional field from section summary
 *
 * @param summary CWTSchemaPropertySummary
 * @param fieldId Field id
 */
export function removeFieldFromSectionSummary(summary: CWTSchemaPropertySummary, fieldId: string): CWTSchemaPropertySummary {
    const updatedParams = summary.calculation.return.params.filter(
        (p) => (isReference(p) && p.ref.replace('.__self__.', '.item.').substring(1) !== fieldId) || !isReference(p)
    );

    const newSummary: CWTSchemaPropertySummary = {
        ...summary,
        calculation: { ...summary.calculation, return: { ...summary.calculation.return, params: updatedParams as CWTRef[] } },
    };

    return newSummary;
}

/**
 * Add new additional field to section summary
 *
 * @param summary CWTSchemaPropertySummary
 * @param fieldId Field id
 */
export function addFieldToSectionSummary(summary: CWTSchemaPropertySummary, fieldId: string): CWTSchemaPropertySummary {
    const newSummary: CWTSchemaPropertySummary = {
        ...summary,
        calculation: {
            ...summary.calculation,
            return: {
                ...summary.calculation.return,
                params: [...summary.calculation.return.params, { ref: `#${fieldId.replace('.item.', '.__self__.')}` }] as CWTRef[],
            },
        },
    };

    return newSummary;
}

/**
 * Update section to allow summary row
 *
 * @param section CWTSchemaSection
 * @param type FieldType
 */
export function updateSectionSummary(section: CWTSchemaSection, type?: FieldType, prevReference?: string): CWTSchemaPropertySummary | null {
    // If a section previously has summary and no type is specified, it means that summary is being toggled off
    if (section.summary !== null && section.summary !== undefined && !type) {
        return null;
    }

    const constraintType = type || FieldType.MONEY;

    // Get section field ids that matches the constraint type
    const matchingFieldIds = Object.keys(section.properties)
        .filter((key) => section.properties[key].type === constraintType)
        .map((k) => section.properties[k].id);

    const prevDependentFields =
        prevReference && section.summary?.properties[prevReference]?.dependentFields
            ? section.summary?.properties[prevReference]?.dependentFields
            : [];

    // Build section summary object
    const summary: CWTSchemaPropertySummary = {
        id: `${section.id}.summary.properties.${getReference(section.id)}`,
        name: 'Total',
        type: constraintType,
        value: null,
        calculation: {
            type: 'FUNCTION',
            return: {
                operation: 'SUM',
                params: matchingFieldIds.map((id) => ({ ref: `#${id.replace('.item.', '.__self__.')}` })),
            },
        },
        reconciliation: null,
        dependentFields: prevDependentFields || [],
    };

    return summary;
}

/**
 * Unpin any existing pinned fields within a section
 *
 * @param section CWTSchemaSection
 * @returns Update section fields with no pinned fields
 */
export function unPinExistingFields(section: CWTSchemaSection): { [key: string]: CWTSchemaField } {
    Object.keys(section.properties).forEach((k) => {
        if (section.properties[k].pinned === true) {
            section.properties[k].pinned = false;
        }
    });

    return section.properties;
}

/**
 * If a property can be summarised based on property type
 *
 * @param type Property type
 * @returns True if property can be summarised
 */
export function canSummarise(type: ListType | SectionType | TableType | FieldType): boolean {
    return type === FieldType.ACCOUNTING || type === FieldType.MONEY || type === FieldType.NUMBER || type === FieldType.PERCENT;
}

/**
 * Get a section pinned field which can either be a pinned field or section summary property
 *
 * @param section CWTSchemaSection
 * @returns CWTSchemaField | CWTSchemaPropertySummary | null
 */
export function getPinnedField(section: CWTSchemaSection): CWTSchemaField | CWTSchemaPropertySummary | null {
    const sectionSummary = section.summary?.properties[getReference(section.id)];

    if (sectionSummary) {
        return sectionSummary;
    }

    const pinnedFieldKey = Object.keys(section.properties).find((key) => section.properties[key].pinned);

    if (!pinnedFieldKey) {
        return null;
    }

    return section.properties[pinnedFieldKey];
}

type PropertyPath = {
    id: string;
    name: string;
    type: ListType | SectionType | TableType | FieldType;
    summary: CWTSchemaPropertiesSummary | null;
};

/**
 * Build full path for a property
 *
 * @param schema CWTSchema
 * @param property CWTSchemaProperty
 * @returns An array of path consisting of all parent properties that lead to actual property
 */
export function buildPropertyPath(schema: CWTSchema, property: CWTSchemaProperty): PropertyPath[] {
    // Get number of levels for a property (each level has a corresponding id of 'properties.reference')
    const totalPathLevels = property.id.split('.').filter((ref) => ref === 'properties').length;

    const paths: PropertyPath[] = [];

    let i = totalPathLevels - 1;

    /**
     * Go through the full property path, get the actual parent property starting from the top parent (LIST property)
     * Path for each level is made up of 'properties.xxx', hence we slice the id array by doing slice(0, -6) then slice(0, -4)...
     *
     * E.g If a field is made up of 4 levels with the id of 'properties.list1.properties.item.properties.section1.properties.field1'
     *     We will first get the full property of 'properties.list1' then 'properties.list1.properties.section1'
     *     then 'properties.list1.properties.section1.properties.item' then 'properties.list1.properties.section1.properties.item.properties.field1'
     */
    while (i > 0) {
        const parentPropertyId = property.id
            .split('.')
            .slice(0, i * -2)
            .join('.');

        const parentProperty = getProperty(schema, parentPropertyId);

        if (parentProperty) {
            paths.push({
                id: parentProperty.id,
                name: parentProperty.name,
                type: parentProperty.type,
                summary: 'summary' in parentProperty ? parentProperty.summary : null,
            });
        }

        i--;
    }

    // Includes the actual property at the end of array
    paths.push({
        id: property.id,
        name: property.name,
        type: property.type,
        summary: 'summary' in property ? property.summary : null,
    });

    return paths;
}

/**
 * Delete summarised property from list summary
 *
 * @param summary List summary
 * @param referenceToDelete Summarised property reference to be removed
 * @returns Updated list summary properties
 */
export function removePropertyFromListSummary(
    summary: CWTSchemaPropertiesSummary,
    referenceToDelete: string
): CWTSchemaPropertiesSummary | null {
    const updatedSummary = summary.properties;
    delete updatedSummary[referenceToDelete];

    if (Object.keys(updatedSummary).length === 0) {
        return null;
    }

    return { properties: updatedSummary };
}

type AddPropertyToListSummaryInput = {
    list: Pick<CWTSchemaList, 'id' | 'summary'>;
    summaryReference: string;
    summaryName: string;
    property: CWTSchemaProperty;
};

/**
 * Add property to list summary
 *
 * @param list List
 * @param summaryReference Section/field reference
 * @param summaryName Section/field name
 * @param propertyId Id of actual property to be summarised
 * @param propertyFieldType Field type of actual property to be summarised
 * @returns Updated list summary properties
 */
export function addPropertyToListSummary({
    list,
    summaryReference,
    summaryName,
    property,
}: AddPropertyToListSummaryInput): CWTSchemaPropertiesSummary {
    const newSummaryProperty: CWTSchemaPropertySummary = {
        id: `${list.id}.summary.properties.${summaryReference}`,
        name: summaryName,
        type: property.type as FieldType,
        value: null,
        quickActions: 'quickActions' in property ? property.quickActions : undefined,
        calculation: {
            type: 'AGGREGATION',
            return: {
                operation: 'SUM',
                params: [
                    {
                        ref: `#${property.id.replace('properties.item.properties', 'properties.__every__.properties')}`,
                    },
                ],
            },
        },
        reconciliation: null,
        dependentFields: [],
    };

    if (!list.summary) {
        return { properties: { [summaryReference]: newSummaryProperty } };
    }

    const updatedSummary = list.summary.properties;
    updatedSummary[summaryReference] = newSummaryProperty;

    return { properties: updatedSummary };
}

/**
 * Build updated list summary when summarised property reference changes
 *
 * @param schema CWTSchema
 * @param property CWTSchemaProperty
 * @param newId New property id
 * @param prevReference Previous property reference
 * @returns New list summary or undefined
 */
export function updateListSummaryOnReferenceChange(
    schema: CWTSchema,
    property: CWTSchemaProperty,
    prevReference: string
): { listId: string; summary: CWTSchemaPropertiesSummary | null } | undefined {
    const summarisedField = getToSummarisedField(property);

    if (summarisedField && NUMERIC_TYPES.includes(summarisedField.fieldType)) {
        const propertyPath = buildPropertyPath(schema, property);
        const listParent = propertyPath.find((p) => p.type === ListType.LIST);

        // If field belongs in a summary, check if field is included in list summary
        if (listParent && listParent.summary && listParent.summary.properties[prevReference]) {
            let updatedSummary: CWTSchemaPropertiesSummary | null = listParent.summary;

            const prop = getProperty(schema, summarisedField.id) as CWTSchemaProperty;

            updatedSummary = addPropertyToListSummary({
                list: listParent,
                summaryReference: getReference(property.id),
                summaryName: property.name,
                property: prop,
            });
            updatedSummary = removePropertyFromListSummary(listParent.summary, prevReference);
            return { listId: listParent.id, summary: updatedSummary };
        }
    }
}

/**
 * Build updated list summary when summarised property type changes
 *
 * @param schema CWTSchema
 * @param property CWTSchemaProperty
 * @returns New list summary or undefined
 */
export function updateListSummaryOnTypeChange(
    schema: CWTSchema,
    property: CWTSchemaSection | CWTSchemaField
): { listId: string; summary: CWTSchemaPropertiesSummary | null } | undefined {
    const summarisedField = getToSummarisedField(property);

    if (!summarisedField) {
        return;
    }

    const propertyRef = getReference(property.id);
    const propertyPath = buildPropertyPath(schema, property);
    const listParent = propertyPath.find((p) => p.type === ListType.LIST);

    // If field belongs in a summary, check if field is included in list summary
    if (listParent && listParent.summary && listParent.summary.properties[propertyRef]) {
        let updatedSummary: CWTSchemaPropertiesSummary | null = listParent.summary;

        const prop = getProperty(schema, summarisedField.id) as CWTSchemaProperty;

        // If new type can be summarised, update in list summary
        if (NUMERIC_TYPES.includes(summarisedField.fieldType)) {
            updatedSummary = addPropertyToListSummary({
                list: listParent,
                summaryReference: getReference(property.id),
                summaryName: property.name,
                property: prop,
            });
        }
        // Else delete field from list summary
        else {
            updatedSummary = removePropertyFromListSummary(listParent.summary, propertyRef);
        }

        return { listId: listParent.id, summary: updatedSummary };
    }
}

/**
 * Get field id and type for property to be added to list summary
 * It can be either a section summary value OR section pinned field OR field property
 *
 * @param property CWTSchemaProperty
 * @returns Id and field type of field to be summarised
 */
export function getToSummarisedField(property: CWTSchemaProperty): { id: string; fieldType: FieldType } | undefined {
    if (isCWTSchemaSection(property)) {
        const pinnedField = getPinnedField(property);

        if (!pinnedField) {
            return;
        }

        return { id: pinnedField.id, fieldType: pinnedField.type };
    }

    if (isCWTSchemaField(property)) {
        return { id: property.id, fieldType: property.type };
    }
}

/**
 * Update property id and reference values in calculation/dependent fields
 *
 * @param schema CWTSchema
 * @param prevId Previous property id
 * @param newId New property id
 */
function updateDependentFields(schema: CWTSchema, prevId: string, newId: string): void {
    /**
     * Regex to match property id to prevent partial matching
     * properties.section1 matches as a prefix to properties.section1.properties.field
     * properties.section1 DOES NOT match as a prefix to properties.section123.properties.field
     */
    const re = new RegExp('^' + prevId.replace('.__self__.', '.item.').replace('.__every__.', '.row.') + '($|\\.)');

    traverse(schema).forEach(function (p) {
        // If p is an id that starts with previous path, replace with new path
        if (typeof p === 'string' && re.test(p)) {
            this.update(p.replace(prevId, newId));
        }

        if (isReference(p)) {
            /**
             * List summary calculation reference could be 'properties.list1.properties.__every__.properties.field1' so it needs to be converted back to 'properties.list1.properties.item.properties.field1'
             * Table summary calculation reference in a list could be 'properties.list1.properties.__self__.properties.table1.properties.__every__.properties.column1' so it needs to be converted back to 'properties.list1.properties.item.properties.table1.properties.row.properties.column1'
             */
            if (
                re.test(p.ref.replace('.__self__.', '.item.').replace('.__every__.', '.row.').substring(1)) ||
                re.test(p.ref.replace('.__every__.', '.item.').substring(1))
            ) {
                this.update({
                    ref: p.ref.replaceAll(getReference(prevId), getReference(newId)),
                });
            }
        }
    });
}

/**
 * Build summary object for a table row property
 *
 * @param summarise Whether a table row is summarised
 * @param id Table row id
 * @returns CWTSchemaPropertySummary | null
 */
export function buildSummaryObject(
    id: string,
    type: FieldType,
    summarise: boolean,
    reconciliation: CWTFieldReconciliation | null = null
): CWTSchemaPropertySummary | null {
    if (summarise) {
        return {
            id: id.replace('.properties.row.', '.summary.'),
            name: 'Total',
            type,
            value: null,
            calculation: {
                type: 'AGGREGATION',
                return: {
                    operation: 'SUM',
                    params: [
                        {
                            ref: `#${id.replace('properties.row.properties', 'properties.__every__.properties')}`.replace(
                                'properties.item.properties',
                                'properties.__self__.properties'
                            ),
                        },
                    ],
                },
            },
            reconciliation,
            dependentFields: [],
        };
    }

    return null;
}
